import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import { Checkbox } from '@components/checkbox';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';

const enumOptions = (emptyLabel: string, values: string[]) => [
    { label: emptyLabel, value: undefined },
    ...values.map((value) => ({ label: formatEnumLabel(value), value }))
];

const parseOptionalNumber = (value: string | number) =>
    value === '' ? undefined : Number(value);

export interface ActivityFilterFormValue {
    activityType?: string;
    workoutCategory?: string;
    workoutType?: string;
    hasArrivalLocation?: boolean;
    minRawValue?: number;
    maxRawValue?: number;
    fitnessMetricType?: string;
}

export const createEmptyActivityFilter = (): ActivityFilterFormValue => ({
    activityType: undefined,
    workoutCategory: undefined,
    workoutType: undefined,
    hasArrivalLocation: undefined,
    minRawValue: undefined,
    maxRawValue: undefined,
    fitnessMetricType: undefined
});

export const formatEnumLabel = (value: string): string =>
    value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());

export const normalizeActivityFilters = (
    filters: ActivityFilterFormValue[],
    workoutActivityType: string
): ActivityFilterFormValue[] =>
    filters
        .map((filter) => {
            const activityType = filter.activityType;
            const isWorkout = activityType === workoutActivityType;

            return {
                activityType,
                workoutCategory: isWorkout ? filter.workoutCategory : undefined,
                workoutType: isWorkout ? filter.workoutType : undefined,
                hasArrivalLocation: isWorkout
                    ? filter.hasArrivalLocation || undefined
                    : undefined,
                minRawValue: filter.minRawValue || undefined,
                maxRawValue: filter.maxRawValue || undefined,
                fitnessMetricType: filter.fitnessMetricType
            };
        })
        .filter(
            (filter) =>
                filter.activityType ||
                filter.workoutCategory ||
                filter.workoutType ||
                filter.hasArrivalLocation ||
                filter.minRawValue ||
                filter.maxRawValue ||
                filter.fitnessMetricType
        );

export interface ActivityFiltersEditorProps {
    value: ActivityFilterFormValue[];
    onChange: (value: ActivityFilterFormValue[]) => void;
    activityTypeOptions: string[];
    workoutCategoryOptions: string[];
    workoutTypeOptions: string[];
    fitnessMetricOptions: string[];
    workoutActivityType: string;
    title?: string;
    helperText?: string;
    emptyStateText?: string;
    disabled?: boolean;
    showMaxRawValue?: boolean;
}

export const ActivityFiltersEditor: React.FC<ActivityFiltersEditorProps> = ({
    value,
    onChange,
    activityTypeOptions,
    workoutCategoryOptions,
    workoutTypeOptions,
    fitnessMetricOptions,
    workoutActivityType,
    title = 'Counted activity sets',
    helperText,
    emptyStateText,
    disabled = false,
    showMaxRawValue = false
}) => {
    const filters = value;

    const updateFilter = (
        index: number,
        patch: Partial<ActivityFilterFormValue>
    ) => {
        onChange(
            filters.map((filter, filterIndex) =>
                filterIndex === index
                    ? {
                          ...filter,
                          ...patch
                      }
                    : filter
            )
        );
    };

    const handleActivityTypeChange = (index: number, activityType?: string) => {
        const isWorkout = activityType === workoutActivityType;

        updateFilter(index, {
            activityType,
            workoutCategory: isWorkout
                ? filters[index].workoutCategory
                : undefined,
            workoutType: isWorkout ? filters[index].workoutType : undefined,
            hasArrivalLocation: isWorkout
                ? filters[index].hasArrivalLocation
                : undefined
        });
    };

    const addFilter = () => {
        onChange([...filters, createEmptyActivityFilter()]);
    };

    const removeFilter = (index: number) => {
        onChange(filters.filter((_, filterIndex) => filterIndex !== index));
    };

    return (
        <div>
            <div className="mb-3 flex items-center justify-between gap-4">
                <div>
                    <h3 className="text-base font-semibold">{title}</h3>
                    <p className="text-sm text-muted-foreground">
                        {helperText ||
                            'Add one or more activity filters. Multiple rows are matched with OR semantics.'}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={addFilter}
                    disabled={disabled}
                >
                    <Plus />
                    Add Set
                </Button>
            </div>

            {filters.length === 0 ? (
                <div className="rounded-xl border border-dashed p-4">
                    <p className="text-sm text-muted-foreground">
                        {emptyStateText ||
                            'No counted activity sets configured. The goal will match all activities supported by the selected rule type.'}
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {filters.map((filter, index) => {
                        const isWorkout =
                            filter.activityType === workoutActivityType;

                        return (
                            <Card key={index} className="py-4 shadow-none">
                                <CardContent className="flex flex-col gap-3 px-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">
                                            Set {index + 1}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            className="text-destructive"
                                            onClick={() => removeFilter(index)}
                                            disabled={disabled}
                                        >
                                            <Trash2 />
                                        </Button>
                                    </div>
                                    <div className="grid gap-3 md:grid-cols-3">
                                        <Select
                                            label="Activity Type"
                                            disabled={disabled}
                                            value={filter.activityType}
                                            options={enumOptions(
                                                'All activity types',
                                                activityTypeOptions
                                            )}
                                            onChange={(activityType) =>
                                                handleActivityTypeChange(
                                                    index,
                                                    activityType
                                                )
                                            }
                                        />
                                        <Select
                                            label="Workout Category"
                                            disabled={disabled || !isWorkout}
                                            value={
                                                isWorkout
                                                    ? filter.workoutCategory
                                                    : undefined
                                            }
                                            options={enumOptions(
                                                'All workout categories',
                                                workoutCategoryOptions
                                            )}
                                            onChange={(workoutCategory) =>
                                                updateFilter(index, {
                                                    workoutCategory
                                                })
                                            }
                                        />
                                        <Select
                                            label="Workout Type"
                                            disabled={disabled || !isWorkout}
                                            value={
                                                isWorkout
                                                    ? filter.workoutType
                                                    : undefined
                                            }
                                            options={enumOptions(
                                                'All workout types',
                                                workoutTypeOptions
                                            )}
                                            onChange={(workoutType) =>
                                                updateFilter(index, {
                                                    workoutType
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="grid gap-3 md:grid-cols-3">
                                        <Select
                                            label="Fitness Metric"
                                            disabled={disabled}
                                            value={filter.fitnessMetricType}
                                            options={enumOptions(
                                                'Any metric',
                                                fitnessMetricOptions
                                            )}
                                            onChange={(fitnessMetricType) =>
                                                updateFilter(index, {
                                                    fitnessMetricType
                                                })
                                            }
                                        />
                                        <TextField
                                            type="number"
                                            label="Min Raw Value"
                                            disabled={disabled}
                                            value={filter.minRawValue ?? ''}
                                            onChange={(value) =>
                                                updateFilter(index, {
                                                    minRawValue:
                                                        parseOptionalNumber(
                                                            value
                                                        )
                                                })
                                            }
                                        />
                                        {showMaxRawValue && (
                                            <TextField
                                                type="number"
                                                label="Max Raw Value"
                                                disabled={disabled}
                                                value={filter.maxRawValue ?? ''}
                                                onChange={(value) =>
                                                    updateFilter(index, {
                                                        maxRawValue:
                                                            parseOptionalNumber(
                                                                value
                                                            )
                                                    })
                                                }
                                            />
                                        )}
                                    </div>
                                    <Checkbox
                                        center
                                        checked={
                                            isWorkout &&
                                            !!filter.hasArrivalLocation
                                        }
                                        disabled={disabled || !isWorkout}
                                        onChange={(_, checked) =>
                                            updateFilter(index, {
                                                hasArrivalLocation:
                                                    checked || undefined
                                            })
                                        }
                                    >
                                        Only workouts ending at arrival location
                                    </Checkbox>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
